import React, { useState } from 'react';
import { FaTimes, FaFileImport } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';
import { downloadSampleCsv } from '../../utils/csvSamples';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:6990';

export default function ImportModal({ onClose, onImported }) {
  const modal = useModal();

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  function parseCSVPreview(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
    return lines.slice(0, 5).map((line) => line.split(','));
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      setPreview(parseCSVPreview(text));
    };
    reader.readAsText(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    setSelectedFile(file);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      setPreview(parseCSVPreview(text));
    };
    reader.readAsText(file);
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  async function handleImport() {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);

    setImporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/contacts/import`, {
        method: 'POST',
        credentials: 'include', // Send httpOnly cookies
        body: formData,
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json?.message || 'Import failed.');
      }

      const { created = 0, updated = 0, skipped = 0 } = json;
      setResults({ created, updated, skipped });
      modal.alert(`Import complete. Created: ${created}, Updated: ${updated}, Skipped: ${skipped}`);
      onImported();
    } catch (err) {
      modal.alert(err?.message || 'An error occurred during import.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-surface-card rounded-xl shadow-2xl w-[440px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <FaFileImport className="text-primary" />
            Import Leads from CSV
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* File drop area */}
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => document.getElementById('csv-file-input').click()}
          >
            <FaFileImport className="mx-auto text-3xl text-gray-400 mb-2" />
            {selectedFile ? (
              <p className="text-sm font-medium text-gray-700">{selectedFile.name}</p>
            ) : (
              <>
                <p className="text-sm text-gray-500">Click to select or drag a CSV file here</p>
                <p className="text-xs text-gray-400 mt-1">Accepts .csv, .txt</p>
              </>
            )}
            <input
              id="csv-file-input"
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">
                Preview (first {preview.length} row{preview.length !== 1 ? 's' : ''})
              </p>
              <div className="overflow-x-auto rounded border">
                <table className="text-xs w-full border-collapse">
                  <tbody>
                    {preview.map((row, rowIdx) => (
                      <tr key={rowIdx} className={rowIdx === 0 ? 'bg-gray-50 font-medium' : ''}>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="border px-2 py-1 whitespace-nowrap max-w-[120px] truncate"
                            title={cell}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Helper text */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Expected columns: <span className="font-medium">phone</span> (required), firstName, lastName, email, companyName, tags
            </p>
            <button
              type="button"
              onClick={() => downloadSampleCsv('contacts')}
              className="text-xs text-primary hover:underline font-medium"
            >
              Download sample CSV
            </button>
          </div>

          {/* Results */}
          {results && (
            <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              Import complete &mdash; Created: <strong>{results.created}</strong>, Updated:{' '}
              <strong>{results.updated}</strong>, Skipped: <strong>{results.skipped}</strong>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!selectedFile || importing}
            className="btn-primary px-4 py-2 text-sm rounded-lg disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}
